import type { IFile, IOrder, IService, IServiceAdditionalService, IServiceCategory, ITarget } from '@types'
import { ServiceOfferMode, ServiceTypes } from '@types'
import { isFilePhoto } from './company'
import { genId } from './genId'
import { hmToTMinutes } from './minutes'

export interface ServicesByType {
  list: IService[]
  main: IService[]
  extra: IServiceAdditionalService[]
  inventory: IService[]
  byId: Record<string, IService>
}

export const CORE_SERVICE_TYPES = [ServiceTypes.Individual, ServiceTypes.Rent, ServiceTypes.GroupService]

export function isCoreService(s: IService) {
  return CORE_SERVICE_TYPES.includes(s.type)
}

export function isInventoryMainService(service: Pick<IService, 'type' | 'offerMode'>) {
  return (
    service.type === ServiceTypes.Inventory
    && (service.offerMode === ServiceOfferMode.Both || service.offerMode === ServiceOfferMode.Main)
  )
}

export function isInventoryAddonService(service: Pick<IService, 'type' | 'offerMode'>) {
  return (
    service.type === ServiceTypes.Inventory
    && (service.offerMode === ServiceOfferMode.Both || service.offerMode === ServiceOfferMode.Addon)
  )
}

export function isDurationlessService(service: Pick<IService, 'type'>) {
  return [ServiceTypes.Individual, ServiceTypes.GroupService].includes(service.type)
}

export function getNextTargetSortPosition(targets: ITarget[] = []) {
  return Math.max(0, ...targets.map(target => target.sortPosition ?? 0)) + 1
}

export function sortCategories(categories: IServiceCategory[]) {
  return Array.from(categories).sort((a, b) => (a.sortPosition ?? a.id) - (b.sortPosition ?? b.id))
}

function sortTargets(targets: ITarget[] = []) {
  return targets.sort((a, b) => (a.sortPosition ?? a.id) - (b.sortPosition ?? b.id))
}

function enrichServicesWithExtraServices(service: IService, extra: IServiceAdditionalService[]) {
  service.targets = sortTargets(service.targets)
  service.additionalServices = filterActiveServices(service.additionalEnableServices || []).reduce<
    IServiceAdditionalService[]
  >((result, aes) => {
    const additionalService = extra.find(({ id }) => id === aes.childrenId)
    if (additionalService) {
      result.push({
        ...additionalService,
        isRequire: aes.isRequire,
        price: aes.price != null ? aes.price / 100 : additionalService.price,
      })
    }
    return result
  }, [])
  delete service.additionalEnableServices
  return service
}

function linkServicesWithAddOns(service: IService, addOns: IService[]) {
  ;(service.additionalEnableServices || [])
    ?.filter(({ childrenId: id }) => addOns.some(s => s.id === id))
    .forEach(aes => {
      const additionalService = addOns.find(({ id }) => id === aes.childrenId)
      if (!additionalService) {
        return
      }

      additionalService.additionalEnableServices ||= []
      const index = additionalService.additionalEnableServices.findIndex(
        relation => relation.parentId === aes.parentId && relation.childrenId === aes.childrenId,
      )
      if (index === -1) {
        additionalService.additionalEnableServices.push(aes)
      } else {
        additionalService.additionalEnableServices[index] = aes
      }
    })
  return service
}

export function getServicesCatalog(services: IService[]) {
  services.forEach(s => {
    s.targets = sortTargets(s.targets)
  })

  const list = sortServices(services)
  const extra = filterExtraServices(list)
  const inventory = filterInventoryServices(list)
  return {
    list,
    extra,
    inventory,
    main: filterMainServices(list).map(service => linkServicesWithAddOns(service, [...extra, ...inventory])),
    byId: list.reduce<Record<string, IService>>((acc, service) => {
      acc[service.id] = service
      return acc
    }, {}),
  }
}

function filterActiveServices<T extends Pick<IService, 'customerCanView' | 'isRequire'>>(services: T[]) {
  return services.filter(s => s.customerCanView || s.isRequire)
}

// export function filterActiveServicesInCatalog(catalog: ServicesByType) {
//   return {
//     extra: filterActiveServices(catalog.extra),
//     main: filterActiveServices(catalog.main).map(s => ({
//       ...s,
//       additionalServices: filterActiveServices(s.additionalServices)
//     })),
//   };
// }

export function sortFiles(files: IFile[]) {
  return files.sort((a, b) => a.sortPosition - b.sortPosition)
}

export function sortServices(services: IService[]) {
  return services.sort((a, b) => (a.sortPosition ?? a.id) - (b.sortPosition ?? b.id))
}

export function getEnrichedMainServices(services: IService[]) {
  const extra = sortServices(filterExtraServices(services))
  return sortServices(filterActiveServices(filterMainServices(services))).map(service => {
    enrichServicesWithExtraServices(service, extra)
    service.additionalServices = sortServices(service.additionalServices)
    return service
  })
}

export { ITargetType as TargetType } from '@types'

function fixMainServicesSettings(services: IService[]) {
  services.forEach(service => {
    service.priceSettings?.forEach(setting => {
      setting.parameters.forEach(param => {
        param.timeStart = +param.timeStart
        param.timeEnd = +param.timeEnd
        if (isDurationlessService(service)) {
          delete param.durationMinutes
        } else {
          param.durationMinutes = hmToTMinutes(param.durationMinutes, param.durationHour)
        }
        delete param.durationHour

        param.id ||= genId()
      })
      setting.id ||= genId()
    })
    service.files = sortFiles(service.files || [])
  })
}

function filterMainServices(services: IService[]) {
  const filtered = services.filter(isCoreService)
  fixMainServicesSettings(filtered)
  return filtered
}

export function filterExtraServices(services: IService[]) {
  return services.filter(s => s.type === ServiceTypes.Additional)
}

export function filterInventoryServices(services: IService[]) {
  return services.filter(s => s.type === ServiceTypes.Inventory)
}

export function getOrderAdditionalServices(item: IOrder) {
  const service = item.orderServices[0]
  const res: string[] = []
  if (service) {
    if (service?.additionalServices) {
      res.push(...service.additionalServices.map(s => s.service.title))
    }
    if (service?.freeAdditionalServices) {
      res.push(...service.freeAdditionalServices.map(s => s.service.title))
    }
  }
  return res.join(', ')
}

export function getPhotos(service: IService) {
  return service.files?.filter(isFilePhoto) || []
}

export function setPhotos(service: IService, photos: IFile[]) {
  const notPhotos = service.files?.filter(f => !isFilePhoto(f)) || []
  return (service.files = [...notPhotos, ...photos])
}
